/**
 * @fileoverview Métadonnées publiques du Design System GNPC.
 *
 * Ce module est volontairement consommable par les humains, les IDE et les
 * outils d'analyse du code : il expose la version courante, les règles de
 * documentation et un inventaire structuré des composants livrés. La page
 * `/design-system` l'affiche, mais la source de vérité vit ici dans `@gnpc/ui`.
 *
 * @see docs/design-system-catalog.md
 */

/**
 * Statut de stabilité d'une API publique du design system.
 *
 * - `stable` : contrat utilisable dans les écrans métier sans validation
 *   design supplémentaire.
 * - `beta` : utilisable, mais l'API peut bouger avant la V1.
 * - `draft` : visible dans la vitrine pour discussion, pas encore à figer.
 */
export type DesignSystemStability = 'stable' | 'beta' | 'alpha' | 'draft';

/**
 * Métadonnées de version du design system.
 */
export interface DesignSystemVersion {
  /** Nom court affiché dans la page de documentation. */
  name: string;
  /** Version sémantique du design system `@gnpc/ui`. */
  version: string;
  /** Date ISO de gel de cette version. */
  releasedAt: string;
  /** Statut de maturité global de la version. */
  stability: DesignSystemStability;
  /** Résumé humain du périmètre livré. */
  summary: string;
}

/**
 * Règle de documentation obligatoire pour les exports publics.
 */
export interface DesignSystemDocumentationRule {
  /** Identifiant stable de la règle. */
  id: string;
  /** Nom court de la règle. */
  label: string;
  /** Critère concret attendu dans le code ou la preview. */
  requirement: string;
}

/**
 * Fiche structurée d'un composant livré.
 */
export interface DesignSystemComponentDoc {
  /** Nom exporté depuis `@gnpc/ui`. */
  name: string;
  /** Famille fonctionnelle utilisée dans la navigation DS. */
  category: 'primitive' | 'form' | 'feedback' | 'overlay' | 'navigation' | 'data' | 'utility';
  /** Statut de stabilité de l'API. */
  stability: DesignSystemStability;
  /** Usage principal attendu. */
  purpose: string;
  /** Props ou sous-composants les plus importants à connaître. */
  api: readonly string[];
  /** Contraintes métier, accessibilité ou sécurité à respecter. */
  constraints: readonly string[];
  /** Section de la vitrine où voir le composant en action. */
  previewSectionId: string;
}

/**
 * Version courante du design system GNPC.
 *
 * @example
 * ```tsx
 * import { DESIGN_SYSTEM_VERSION } from '@gnpc/ui';
 *
 * console.info(DESIGN_SYSTEM_VERSION.version);
 * ```
 */
export const DESIGN_SYSTEM_VERSION = {
  name: 'GNPC Design System',
  version: '0.1.0-alpha.9',
  releasedAt: '2026-07-16',
  stability: 'alpha',
  summary:
    'Alpha 9 : catalogue resynchronisé avec le code — fiches OperatorLogo, CopyButton, FormField, LiquidationNumber, Tabs et DataCard ; logos opérateurs mobile money, démo Tabs et DangerNotice dans la vitrine.',
} as const satisfies DesignSystemVersion;

/**
 * Règles minimales avant de considérer un composant comme livré.
 *
 * @example
 * ```ts
 * DESIGN_SYSTEM_DOCUMENTATION_RULES.map((rule) => rule.requirement);
 * ```
 */
export const DESIGN_SYSTEM_DOCUMENTATION_RULES: readonly DesignSystemDocumentationRule[] = [
  {
    id: 'file-overview',
    label: 'Header de fichier',
    requirement:
      'Chaque module public décrit son rôle, ses invariants GNPC et ses références projet.',
  },
  {
    id: 'export-tsdoc',
    label: 'TSDoc sur chaque export',
    requirement:
      'Chaque composant, type, interface, constante et hook exporté possède un commentaire TSDoc.',
  },
  {
    id: 'props-contract',
    label: 'Contrat de props',
    requirement:
      'Chaque prop publique indique sa valeur attendue, son format canonique et son comportement par défaut.',
  },
  {
    id: 'example',
    label: 'Exemple IDE',
    requirement:
      'Chaque composant public fournit au moins un bloc `@example` copiable dans une app React.',
  },
  {
    id: 'preview',
    label: 'Preview vivante',
    requirement: 'Chaque composant livré apparaît dans `/design-system` avec ses états principaux.',
  },
];

/**
 * Catalogue structuré des composants actuellement livrés.
 *
 * Les outils d'analyse du code peuvent utiliser cette liste pour choisir un
 * composant sans parcourir toute la vitrine visuelle. Les IDE exposent aussi
 * cette constante dans l'auto-complétion quand un développeur importe `@gnpc/ui`.
 */
export const DESIGN_SYSTEM_COMPONENTS: readonly DesignSystemComponentDoc[] = [
  {
    name: 'Button',
    category: 'primitive',
    stability: 'beta',
    purpose: 'Action principale, secondaire, destructive ou icon-only.',
    api: ['variant', 'size', 'asChild'],
    constraints: ['Utiliser une icône Lucide pour les actions icon-only.', 'Hauteur >= 36 px.'],
    previewSectionId: 'buttons',
  },
  {
    name: 'Input',
    category: 'form',
    stability: 'beta',
    purpose: 'Champ texte GNPC avec tailles, slots icônes et état erreur ARIA.',
    api: ['size', 'leftIcon', 'rightIcon', 'aria-invalid'],
    constraints: ['Préférer FormField autour des champs métier.', 'Taille md = 44 px.'],
    previewSectionId: 'inputs',
  },
  {
    name: 'FormField',
    category: 'form',
    stability: 'beta',
    purpose: 'Champ standardisé : label, tooltip d’aide, hint, erreur et action associée.',
    api: ['label', 'required', 'tooltip', 'hint', 'error', 'action', 'children'],
    constraints: [
      'Génère id, aria-invalid et aria-describedby pour l’enfant.',
      'error masque hint quand les deux sont fournis.',
      'À préférer à un Label + Input assemblés à la main.',
    ],
    previewSectionId: 'inputs',
  },
  {
    name: 'SearchInput',
    category: 'form',
    stability: 'beta',
    purpose: 'Recherche dense pour toolbars DataTable avec icône et bouton clear.',
    api: ['value', 'onChange', 'onClear', 'size'],
    constraints: [
      'Utiliser dans les filtres backoffice avant DataTable.',
      'Ne pas gérer le debounce ici.',
    ],
    previewSectionId: 'inputs',
  },
  {
    name: 'PasswordInput',
    category: 'form',
    stability: 'beta',
    purpose: 'Saisie de mot de passe backoffice avec bouton afficher/masquer.',
    api: ['minLength', 'autoComplete', 'revealLabel', 'hideLabel'],
    constraints: [
      'Toujours suivi par MFA TOTP côté backoffice.',
      'Message login identique quel que soit l’échec.',
    ],
    previewSectionId: 'inputs',
  },
  {
    name: 'ReasonTextarea',
    category: 'form',
    stability: 'beta',
    purpose: 'Motif obligatoire des actions sensibles : rejeu, remboursement, reset MFA.',
    api: ['value', 'onChange', 'minReasonLength', 'showCounter'],
    constraints: ['Longueur minimale de 10 caractères.', 'Journalisation obligatoire.'],
    previewSectionId: 'inputs',
  },
  {
    name: 'PhoneNumberInput',
    category: 'form',
    stability: 'beta',
    purpose: 'Saisie téléphone Côte d’Ivoire au format E.164.',
    api: ['value', 'onChange', 'size'],
    constraints: [
      'onChange émet +225XXXXXXXXXX seulement à 10 chiffres.',
      'Aucun pays configurable en V1.',
    ],
    previewSectionId: 'domain-inputs',
  },
  {
    name: 'OtpInput',
    category: 'form',
    stability: 'beta',
    purpose: 'Saisie OTP segmentée pour SMS et vérification courte.',
    api: ['value', 'onChange', 'onComplete', 'length'],
    constraints: ['Valeur canonique = chiffres uniquement.', 'Collage complet supporté.'],
    previewSectionId: 'domain-inputs',
  },
  {
    name: 'TotpInput',
    category: 'form',
    stability: 'beta',
    purpose: 'Saisie MFA TOTP compatible Google Authenticator.',
    api: ['value', 'onChange', 'issuerLabel', 'recoveryActionLabel'],
    constraints: ['Toujours 6 chiffres.', 'Message d’échec identique côté écran auth.'],
    previewSectionId: 'domain-inputs',
  },
  {
    name: 'QrCode',
    category: 'utility',
    stability: 'beta',
    purpose: 'QR code SVG scannable pour enrôlement MFA ou références techniques.',
    api: ['value', 'size', 'errorCorrectionLevel', 'label', 'description'],
    constraints: [
      'Utiliser `otpauth://` pour MFA.',
      'Ne pas exposer de secret hors flux d’enrôlement.',
    ],
    previewSectionId: 'domain-inputs',
  },
  {
    name: 'PlateInput',
    category: 'form',
    stability: 'beta',
    purpose: 'Saisie plaque Côte d’Ivoire, affichée `AB 572 GG`, canonique `AB572GG`.',
    api: ['value', 'onChange', 'aria-invalid'],
    constraints: ['Seul point d’entrée visible du portail tant que LIQ lookup est off.'],
    previewSectionId: 'portal',
  },
  {
    name: 'Amount',
    category: 'data',
    stability: 'beta',
    purpose: 'Affichage XOF entier sans décimales.',
    api: ['value', 'compact', 'hideCurrency', 'formatXof'],
    constraints: ['Jamais de centimes.', 'Devise affichée `F CFA`.'],
    previewSectionId: 'portal',
  },
  {
    name: 'LiquidationNumber',
    category: 'data',
    stability: 'beta',
    purpose: 'Affichage monospace d’un numéro LIQ, copiable à la demande.',
    api: ['value', 'copyable'],
    constraints: [
      'Format canonique `LIQ-921555`.',
      'Activer `copyable` partout où l’usager peut avoir besoin de la référence.',
    ],
    previewSectionId: 'portal',
  },
  {
    name: 'OperatorLogo',
    category: 'data',
    stability: 'stable',
    purpose: 'Logo d’un opérateur mobile money CI à partir de son code OpenAPI.',
    api: ['code', 'size', 'withLabel', 'OPERATOR_LABELS'],
    constraints: [
      'Codes alignés sur l’enum OpenAPI `OperatorCode` (OM_CI, MTN_CI, MOOV_CI, WAVE_CI).',
      '`MOOV_CI` est servi par le logo Flooz, marque mobile money de Moov Africa CI.',
      'code null = fallback neutre « Non renseigné », jamais une erreur.',
      'Console uniquement en V1 : le portail n’affiche pas de sélecteur d’opérateur.',
    ],
    previewSectionId: 'logos',
  },
  {
    name: 'CopyButton',
    category: 'utility',
    stability: 'beta',
    purpose: 'Bouton icône copie-vers-presse-papier avec feedback transitoire.',
    api: ['value', 'ariaLabel', 'size'],
    constraints: [
      'Fournir un `ariaLabel` métier quand la valeur copiée est technique.',
      'Utiliser pour LIQ, références marchand, trace_id et codes couleur.',
    ],
    previewSectionId: 'logos',
  },
  {
    name: 'PageHeader',
    category: 'data',
    stability: 'beta',
    purpose: 'En-tête standard des pages console et portail métier.',
    api: ['eyebrow', 'title', 'description', 'actions', 'meta'],
    constraints: [
      'Pas de carte autour du header.',
      'Le titre décrit le travail à faire, pas une abstraction technique.',
    ],
    previewSectionId: 'cards',
  },
  {
    name: 'SectionHeader',
    category: 'data',
    stability: 'beta',
    purpose: 'Titre compact de section avec action optionnelle.',
    api: ['title', 'description', 'action'],
    constraints: [
      'À utiliser dans les panneaux de page.',
      'Ne pas imbriquer dans une autre card décorative.',
    ],
    previewSectionId: 'cards',
  },
  {
    name: 'MetricCard',
    category: 'data',
    stability: 'beta',
    purpose: 'Tuile de métrique opérationnelle avec tons neutre, info, succès, warning, danger.',
    api: ['label', 'value', 'description', 'icon', 'tone', 'action', 'trend', 'footer'],
    constraints: [
      'Utiliser `danger` pour `sgi_failed_count`.',
      'Aucune ombre, profondeur par bordures et fonds.',
    ],
    previewSectionId: 'cards',
  },
  {
    name: 'ChartContainer',
    category: 'data',
    stability: 'beta',
    purpose:
      'Wrapper Recharts tokenisé pour dashboards console : couleurs de séries, layout responsive et tooltip GNPC.',
    api: ['config', 'id', 'children', 'ChartTooltip', 'ChartTooltipContent', 'ChartConfig'],
    constraints: [
      'Les séries doivent déclarer leurs labels/couleurs dans `ChartConfig`.',
      'Utiliser `var(--color-serie)` dans les marks Recharts.',
      'Aucune ombre dans les tooltips ou surfaces de charts.',
    ],
    previewSectionId: 'charts',
  },
  {
    name: 'Timeline',
    category: 'data',
    stability: 'beta',
    purpose:
      'Historique vertical des événements métier : paiement, STI, remboursement, export ou audit.',
    api: ['items', 'ariaLabel', 'compact', 'emptyState'],
    constraints: [
      'Les événements sont fournis du plus ancien au plus récent.',
      'Chaque item possède un `id` stable.',
      'Ne pas afficher de payload technique brut dans `description`.',
    ],
    previewSectionId: 'cards',
  },
  {
    name: 'EmptyState',
    category: 'feedback',
    stability: 'beta',
    purpose: 'Bloc d’état vide légitime pour tables, files et pages sans résultat.',
    api: ['icon', 'title', 'description', 'action', 'tone', 'size'],
    constraints: [
      'Un résultat vide n’est pas une erreur.',
      'Ton calme, action explicite si utile.',
    ],
    previewSectionId: 'empty-states',
  },
  {
    name: 'StatusBadge',
    category: 'data',
    stability: 'beta',
    purpose: 'Badges centralisés pour statuts paiement, STI, intégration, remboursement et export.',
    api: ['kind', 'status', 'compact'],
    constraints: ['Ne jamais recréer le mapping enum -> couleur dans les pages.'],
    previewSectionId: 'portal',
  },
  {
    name: 'ContraventionCard',
    category: 'data',
    stability: 'beta',
    purpose: 'Carte citoyenne d’une contravention sélectionnable.',
    api: [
      'liquidationNumber',
      'infractionType',
      'amount',
      'infractionDate',
      'dueDate',
      'selected',
      'action',
    ],
    constraints: [
      'Aucune donnée nominative.',
      'LIQ copiables.',
      'dueDate affiche le délai restant.',
    ],
    previewSectionId: 'portal',
  },
  {
    name: 'PaymentSummary',
    category: 'data',
    stability: 'beta',
    purpose: 'Résumé de panier avec total XOF et limites 300 / 2 000 000.',
    api: ['selectedCount', 'totalAmount', 'minAmount', 'maxAmount', 'action'],
    constraints: ['Le plafond doit inviter à scinder le paiement.', 'Pas de sélecteur opérateur.'],
    previewSectionId: 'portal',
  },
  {
    name: 'PaymentProofActions',
    category: 'feedback',
    stability: 'beta',
    purpose: 'Justificatif de débit et renvoi vers le compte Quipux pour la quittance.',
    api: ['debitNoteAvailable', 'onDownloadDebitNote', 'quipuxAccountUrl'],
    constraints: [
      'Justificatif dès PAID.',
      'Quittance hors GNPC : modale explicative puis lien externe compte Quipux.',
    ],
    previewSectionId: 'portal',
  },
  {
    name: 'PortalState',
    category: 'feedback',
    stability: 'beta',
    purpose:
      'Panneau partagé des états portail : empty, pending, success, error, expired et failed.',
    api: ['status', 'title', 'children', 'action', 'secondaryAction', 'progressValue', 'badge'],
    constraints: [
      'Aucun appel service ni interprétation technique dans le composant.',
      'Utiliser `role=status` pour empty/pending/success et `role=alert` pour error/expired/failed.',
      'Ne jamais afficher un message technique brut dans `children`.',
    ],
    previewSectionId: 'portal',
  },
  {
    name: 'FaqList',
    category: 'feedback',
    stability: 'beta',
    purpose: "Liste de questions fréquentes accessible pour les pages d'aide.",
    api: ['items', 'question', 'answer', 'defaultOpen'],
    constraints: [
      'Utiliser des réponses courtes et actionnables.',
      'Ne pas y placer de messages techniques bruts.',
    ],
    previewSectionId: 'portal',
  },
  {
    name: 'Notice',
    category: 'feedback',
    stability: 'beta',
    purpose: 'Messages citoyens non techniques : info, succès, warning, danger.',
    api: ['variant', 'title', 'children'],
    constraints: [
      'Ton rassurant côté citoyen.',
      'Ne jamais afficher de message technique brut sur le portail.',
    ],
    previewSectionId: 'portal',
  },
  {
    name: 'Tabs',
    category: 'navigation',
    stability: 'beta',
    purpose: 'Navigation par onglets pour segmenter un même contexte de page.',
    api: ['Tabs', 'TabsList', 'TabsTrigger', 'TabsContent'],
    constraints: [
      'Réservé à des vues du même objet métier ; sinon utiliser la navigation latérale.',
      'Le premier onglet porte la vue par défaut la plus utile.',
    ],
    previewSectionId: 'menus',
  },
  {
    name: 'DropdownMenu',
    category: 'overlay',
    stability: 'beta',
    purpose: 'Menu d’actions contextuelles, préférences et sous-menus.',
    api: ['DropdownMenuTrigger', 'DropdownMenuContent', 'DropdownMenuItem', 'DropdownMenuSub'],
    constraints: [
      'Actions destructives en variant destructive.',
      'Ne pas masquer une autorisation métier.',
    ],
    previewSectionId: 'menus',
  },
  {
    name: 'LanguageSwitcher',
    category: 'navigation',
    stability: 'beta',
    purpose: 'Sélecteur de langue contrôlé avec icône globe pour header et footer portail.',
    api: ['value', 'options', 'onValueChange', 'surface', 'align'],
    constraints: [
      'UI-only : ne traduit pas les libellés et ne persiste rien seul.',
      'Utiliser `surface="dark"` sur fond navy et `surface="light"` sur fond clair.',
    ],
    previewSectionId: 'menus',
  },
  {
    name: 'Skeleton',
    category: 'feedback',
    stability: 'beta',
    purpose: 'Placeholder de chargement quand la forme du contenu est connue.',
    api: ['Skeleton', 'SkeletonAvatar', 'SkeletonRow', 'SkeletonCard'],
    constraints: [
      'Préférer Skeleton au Spinner pour le chargement initial de listes.',
      'Pas de shadow.',
    ],
    previewSectionId: 'loading',
  },
  {
    name: 'Spinner',
    category: 'feedback',
    stability: 'beta',
    purpose: 'Attente courte ou indéterminée.',
    api: ['label', 'size'],
    constraints: ['Fournir un label visible quand l’attente concerne une zone entière.'],
    previewSectionId: 'loading',
  },
  {
    name: 'Progress',
    category: 'feedback',
    stability: 'beta',
    purpose: 'Progression bornée pour export, import ou traitement long.',
    api: ['value', 'max', 'label', 'showValue'],
    constraints: ['Valeur bornée entre 0 et max.', 'Utiliser ariaLabel sans label visible.'],
    previewSectionId: 'loading',
  },
  {
    name: 'DataCard',
    category: 'data',
    stability: 'beta',
    purpose: 'Carte compacte pour le rendu mobile des lignes DataTable.',
    api: ['eyebrow', 'title', 'description', 'aside', 'meta', 'footer'],
    constraints: [
      'À utiliser via DataTable.mobileCard sur les listes métier.',
      'Ne pas remplacer une table desktop par une grille de cartes.',
      'Afficher les mêmes actions essentielles que la ligne desktop.',
    ],
    previewSectionId: 'tables',
  },
  {
    name: 'DataTable',
    category: 'data',
    stability: 'beta',
    purpose:
      'Table backoffice avec tri, toolbar, empty state, cartes mobiles et pagination cursor.',
    api: [
      'columns',
      'data',
      'loading',
      'emptyState',
      'toolbar',
      'mobileCard',
      'mobileCardLabel',
      'rowAction',
      'pagination',
    ],
    constraints: [
      'Pagination par cursor opaque uniquement.',
      'Liste vide ≠ erreur.',
      'Fournir mobileCard sur les listes métier visibles sur mobile/tablette étroite.',
      'Si une ligne possède une seule action principale, utiliser rowAction au lieu d’une colonne bouton.',
    ],
    previewSectionId: 'tables',
  },
  {
    name: 'Sidebar',
    category: 'navigation',
    stability: 'beta',
    purpose: 'Navigation console navy Quipux, collapsible icon-only.',
    api: ['SidebarProvider', 'Sidebar', 'SidebarMenuButton', 'SidebarTrigger'],
    constraints: ['Logo blanc en état déployé.', 'Favicon temporaire en état compact.'],
    previewSectionId: 'sidebar',
  },
];
